import { useState } from "react";
import { supabase } from "../lib/supabase";

function DataHubPage({
  appTheme,
  isMobile,
  dataHub,
  setDataHub,
  nutritionTargets,
  setNutritionTargets,
  calculateNutritionTargets,
  userId,
}) {
  const [draft, setDraft] = useState({ ...dataHub });
  const [saved, setSaved] = useState(false);
  const [preview, setPreview] = useState(null);

  function updateDraft(field, value) {
    const updated = { ...draft, [field]: value };
    setDraft(updated);
    const result = calculateNutritionTargets(updated);
    setPreview(result);
  }

  function handleSave() {
    console.log("[DataHub] save pressed — userId:", userId, "draft:", draft);
    setDataHub(draft);
    const result = calculateNutritionTargets(draft);
    console.log("[DataHub] calculated targets:", result);
    if (result) {
      setNutritionTargets(result);
      // Use userId prop instead of supabase.auth.getUser() — getUser() makes a
      // network request to re-verify the JWT, which can trigger onAuthStateChange
      // SIGNED_OUT if the token is near expiry, causing an unwanted sign-out.
      if (userId) {
        console.log("[DataHub] upserting to user_profiles for uid:", userId);
        supabase.from("user_profiles").upsert({
          id: userId,
          daily_calorie_goal: result.calories ?? null,
          daily_protein_goal: result.protein  ?? null,
          daily_carbs_goal:   result.carbs    ?? null,
          daily_fats_goal:    result.fats     ?? null,
          gender:          draft.gender          || null,
          dob:             draft.dob             || null,
          current_weight:  draft.currentWeight   || null,
          goal_weight:     draft.goalWeight       || null,
          height:          draft.height           || null,
          activity_level:  draft.activityLevel    || null,
          goal:            draft.goal             || null,
        }, { onConflict: "id" })
          .then(({ error }) => {
            if (error) {
              console.error("[DataHub] upsert error — message:", error.message, "code:", error.code);
            } else {
              console.log("[DataHub] upsert succeeded");
            }
          });
      } else {
        console.warn("[DataHub] no userId — upsert skipped");
      }
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 14px",
    borderRadius: "14px",
    border: appTheme.accentBorder,
    background: appTheme.inputBackground,
    fontSize: "15px",
    color: appTheme.primaryText,
    outline: "none",
    fontFamily: appTheme.fontBody
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: "700",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: appTheme.secondaryText,
    marginBottom: "6px"
  };

  const sectionStyle = {
    background: appTheme.cardBackground,
    borderRadius: "24px",
    border: appTheme.cardBorder,
    padding: "16px 18px",
    display: "grid",
    gap: "14px"
  };

  const targets = preview || nutritionTargets;
  const hasTargets = targets && targets.calories > 0;

  return (
    <div style={{ display: "grid", gap: "12px" }}>

      {/* Nutrition targets summary */}
      {hasTargets && (
        <div style={{
          ...sectionStyle,
          background: appTheme.accentLight,
          border: appTheme.accentBorder
        }}>
          <div style={{
            fontSize: "12px",
            fontWeight: "700",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: appTheme.accentColor,
            marginBottom: "4px"
          }}>
            Your Daily Targets
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px"
          }}>
            {[
              { label: "Calories", value: `${targets.calories} kcal` },
              { label: "Protein", value: `${targets.protein}g` },
              { label: "Carbs", value: `${targets.carbs}g` },
              { label: "Fats", value: `${targets.fats}g` }
            ].map(({ label, value }) => (
              <div key={label} style={{
                padding: "10px 12px",
                borderRadius: "14px",
                background: appTheme.cardBackground,
                border: appTheme.cardBorder,
                textAlign: "center"
              }}>
                <div style={{
                  fontSize: "16px",
                  fontWeight: "800",
                  color: appTheme.accentColor,
                  lineHeight: 1
                }}>
                  {value}
                </div>
                <div style={{
                  fontSize: "11px",
                  color: appTheme.secondaryText,
                  marginTop: "3px"
                }}>
                  {label}
                </div>
              </div>
            ))}
          </div>
          {targets.bmr && (
            <div style={{
              fontSize: "12px",
              color: appTheme.secondaryText,
              textAlign: "center"
            }}>
              BMR: {targets.bmr} kcal • TDEE: {targets.tdee} kcal
            </div>
          )}
        </div>
      )}

      {/* Biometrics */}
      <div style={sectionStyle}>
        <div style={labelStyle}>Biometrics</div>

        <div>
          <div style={labelStyle}>Gender</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            {[
              { value: "male", label: "Male" },
              { value: "female", label: "Female" }
            ].map(({ value, label }) => (
              <button
                key={value}
                onClick={() => updateDraft("gender", value)}
                style={{
                  padding: "12px",
                  borderRadius: "14px",
                  border: draft.gender === value ? appTheme.accentBorder : appTheme.pillBorder,
                  background: draft.gender === value ? appTheme.accentColor : appTheme.pillBackground,
                  color: draft.gender === value ? "#ffffff" : appTheme.primaryText,
                  fontWeight: draft.gender === value ? "700" : "500",
                  fontSize: "14px",
                  cursor: "pointer",
                  transition: "all 0.18s ease"
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div style={labelStyle}>Date of Birth</div>
          <input
            type="date"
            value={draft.dob}
            onChange={(e) => updateDraft("dob", e.target.value)}
            style={{ ...inputStyle, WebkitAppearance: "none", appearance: "none" }}
          />
        </div>

        <div>
          <div style={labelStyle}>Current Weight (kg)</div>
          <input
            type="number"
            value={draft.currentWeight}
            onChange={(e) => updateDraft("currentWeight", e.target.value)}
            placeholder="e.g. 75"
            style={inputStyle}
          />
        </div>

        <div>
          <div style={labelStyle}>Goal Weight (kg)</div>
          <input
            type="number"
            value={draft.goalWeight}
            onChange={(e) => updateDraft("goalWeight", e.target.value)}
            placeholder="e.g. 70"
            style={inputStyle}
          />
        </div>

        <div>
          <div style={labelStyle}>Height (cm)</div>
          <input
            type="number"
            value={draft.height}
            onChange={(e) => updateDraft("height", e.target.value)}
            placeholder="e.g. 175"
            style={inputStyle}
          />
        </div>
      </div>

      {/* Activity Level */}
      <div style={sectionStyle}>
        <div style={labelStyle}>Activity Level</div>
        <div style={{ display: "grid", gap: "8px" }}>
          {[
            { value: "sedentary", label: "Sedentary", desc: "Little or no exercise" },
            { value: "lightly", label: "Lightly Active", desc: "1–3 days exercise per week" },
            { value: "moderately", label: "Moderately Active", desc: "3–5 days exercise per week" },
            { value: "very", label: "Very Active", desc: "6–7 days hard exercise" }
          ].map(({ value, label, desc }) => (
            <button
              key={value}
              onClick={() => updateDraft("activityLevel", value)}
              style={{
                padding: "12px 14px",
                borderRadius: "14px",
                border: draft.activityLevel === value ? appTheme.accentBorder : appTheme.pillBorder,
                background: draft.activityLevel === value ? appTheme.accentColor : appTheme.pillBackground,
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.18s ease"
              }}
            >
              <div style={{
                fontSize: "14px",
                fontWeight: "700",
                color: draft.activityLevel === value ? "#ffffff" : appTheme.primaryText
              }}>
                {label}
              </div>
              <div style={{
                fontSize: "12px",
                color: draft.activityLevel === value ? "rgba(255,255,255,0.75)" : appTheme.secondaryText,
                marginTop: "2px"
              }}>
                {desc}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Goal */}
      <div style={sectionStyle}>
        <div style={labelStyle}>Your Goal</div>
        <div style={{ display: "grid", gap: "8px" }}>
          {[
            { value: "lose", label: "Lose Weight", desc: "500 calorie daily deficit" },
            { value: "maintain", label: "Maintain Weight", desc: "Eat at your TDEE" },
            { value: "gain", label: "Build Muscle", desc: "300 calorie daily surplus" }
          ].map(({ value, label, desc }) => (
            <button
              key={value}
              onClick={() => updateDraft("goal", value)}
              style={{
                padding: "12px 14px",
                borderRadius: "14px",
                border: draft.goal === value ? appTheme.accentBorder : appTheme.pillBorder,
                background: draft.goal === value ? appTheme.accentColor : appTheme.pillBackground,
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.18s ease"
              }}
            >
              <div style={{
                fontSize: "14px",
                fontWeight: "700",
                color: draft.goal === value ? "#ffffff" : appTheme.primaryText
              }}>
                {label}
              </div>
              <div style={{
                fontSize: "12px",
                color: draft.goal === value ? "rgba(255,255,255,0.75)" : appTheme.secondaryText,
                marginTop: "2px"
              }}>
                {desc}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Save button */}
      <button
        onClick={handleSave}
        style={{
          width: "100%",
          padding: "16px",
          borderRadius: "18px",
          border: "none",
          background: saved ? "rgba(22,163,74,0.85)" : appTheme.accentColor,
          color: "#ffffff",
          fontSize: "15px",
          fontWeight: "700",
          cursor: "pointer",
          transition: "all 0.18s ease"
        }}
      >
        {saved ? "✓ Saved" : "Save & Calculate"}
      </button>

      <div style={{
        padding: "12px 14px",
        borderRadius: "16px",
        background: appTheme.softBackground,
        border: appTheme.cardBorder,
        fontSize: "12px",
        color: appTheme.secondaryText,
        lineHeight: 1.6
      }}>
        Targets update instantly when saved. These power the macro rings on your Home tab.
      </div>

      <div style={{ height: isMobile ? "100px" : "40px" }} />
    </div>
  );
}

export default DataHubPage;